import { useRef, useMemo, forwardRef, useImperativeHandle } from 'react'
import { useFrame } from '@react-three/fiber'
import { Points, AdditiveBlending } from 'three'
import { useAppStore } from '../../store/appStore'

interface ParticleSystemProps {
  count?: number
  size?: number
}

const ParticleSystem = forwardRef<Points, ParticleSystemProps>(
  ({ count = 2000, size = 1.5 }, ref) => {
    const pointsRef = useRef<Points>(null!)
    const { mousePosition } = useAppStore()

    useImperativeHandle(ref, () => pointsRef.current)
  
  const { positions, colors, sizes } = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    
    for (let i = 0; i < count; i++) {
      // Create spherical distribution
      const i3 = i * 3
      const radius = Math.random() * 20 + 10
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      
      positions[i3] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
      positions[i3 + 2] = radius * Math.cos(phi)
      
      // Create subtle white/gray color gradient
      const intensity = 0.7 + Math.random() * 0.3
      colors[i3] = intensity       // Red channel
      colors[i3 + 1] = intensity   // Green channel
      colors[i3 + 2] = intensity   // Blue channel
      
      sizes[i] = Math.random() * size + 0.5
    }
    
    return { positions, colors, sizes }
  }, [count, size])

  useFrame((state) => {
    if (!pointsRef.current) return
    
    const time = state.clock.elapsedTime
    const positions = pointsRef.current.geometry.attributes.position.array as Float32Array
    
    // Reduce frequency of particle updates to improve performance
    if (time % 0.05 < 0.016) { // Update roughly every 3 frames
      for (let i = 0; i < positions.length; i += 6) { // Skip every other particle
        // Gentler floating animation
        positions[i + 1] += Math.sin(time * 0.3 + positions[i] * 0.005) * 0.001
        positions[i] += Math.cos(time * 0.2 + positions[i + 1] * 0.005) * 0.0005
        
        // Reduced mouse interaction
        const dx = positions[i] - mousePosition.x * 5
        const dy = positions[i + 1] - mousePosition.y * 5
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        if (distance < 3) {
          const force = (3 - distance) / 3
          positions[i] += dx * force * 0.005
          positions[i + 1] += dy * force * 0.005
        }
      }
      
      pointsRef.current.geometry.attributes.position.needsUpdate = true
    }
    
    // Minimal rotation
    pointsRef.current.rotation.y += 0.0005
    pointsRef.current.rotation.x += 0.0002
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={count}
          array={sizes}
          itemSize={1}
        />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        vertexColors
        transparent
        opacity={0.8}
        blending={AdditiveBlending}
        sizeAttenuation
      />
    </points>
  )
})

ParticleSystem.displayName = 'ParticleSystem'

export default ParticleSystem
